import type { ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { statValue } from './statValue';
import {
  UNAVAILABLE_DASH,
  UNAVAILABLE_TITLE,
  type DataStatus,
} from '@/features/Strategies/features/SmartStrategyDefinitonForm/hooks/dataStatus';

interface UniverseStatsRowProps {
  region: string;
  stats: {
    countries: number;
    /** Layer-1 distinct-country count. When fewer countries survive the filters
     *  than the universe holds, Country shows "N / countriesInitial". */
    countriesInitial: number;
    constituents: number;
    /** Layer-1 universe size (all constituents before wizard filters). Shown as
     *  the "N / universeSize" denominator once exclusions narrow the count. */
    universeSize: number;
    coverage: number;
    /** Published (full-universe) coverage — the "/ initial" term for Coverage. */
    coverageInitial: number;
    marketCap: number;
    /** Layer-1 total market cap (trillions) — the "/ initial" term for Cap. */
    marketCapInitial: number;
  };
  /**
   * Availability of the genuinely MEASURED numbers — constituents and market
   * cap. Region, countries and coverage are published properties of the index
   * and always render: `stats.countries` falls back to the index's own country
   * coverage until the live, filter-aware count arrives.
   */
  metricsStatus?: DataStatus;
}

const formatMarketCap = (val: number) => `$${val.toFixed(1)}T`;

function formatCoverage(coverage: number): string {
  // Values <= 1 are decimals (e.g. 0.85 → 85%), values > 1 are already percentages
  const pct = coverage <= 1 ? coverage * 100 : coverage;
  // Keep one decimal when there is one: US Market Extended covers 99.5% of the
  // investable universe, and rounding turned that into a flat (wrong) "100%".
  return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(1)}%`;
}

function StatNumber({
  label,
  value,
  status = 'ready',
  skeletonWidth,
}: {
  label: string;
  value: ReactNode;
  status?: DataStatus;
  skeletonWidth: string;
}) {
  return (
    <div className="space-y-0.5">
      <p className="text-[15px] text-muted-foreground whitespace-nowrap">
        {label}
      </p>
      {status === 'loading' ? (
        <Skeleton className={cn('h-4', skeletonWidth)} />
      ) : (
        // An em dash, never a 0: a confident zero is a lie the user has no way
        // to detect, and "0 constituents / $0.0T" for a live index reads as a
        // data problem with the INDEX rather than with our connection to it.
        <p
          className={cn(
            'font-medium text-sm whitespace-nowrap',
            status === 'unavailable'
              ? 'text-muted-foreground'
              : 'text-foreground',
          )}
          title={status === 'unavailable' ? UNAVAILABLE_TITLE : undefined}
        >
          {status === 'unavailable' ? UNAVAILABLE_DASH : value}
        </p>
      )}
    </div>
  );
}

export function UniverseStatsRow({
  region,
  stats,
  metricsStatus,
}: UniverseStatsRowProps) {
  // Each stat shows "current / initial" ONLY when THAT stat is actually
  // narrowed. Constituents / Coverage / Market Cap all move together with any
  // exclusion or scope-narrowing (they track the constituent count); Country
  // only when a WHOLE country drops out — so a single-stock exclusion in a
  // one-country index (e.g. S&P 500) never reads as a pointless "1 / 1".
  const constituentsReduced = stats.constituents < stats.universeSize;
  const countriesReduced = stats.countries < stats.countriesInitial;
  return (
    // Figma: stats grid divided by a TOP border (border-border/30), no
    // vertical padding, Region column auto-sized, 12px gap. Labels render
    // at 15px (not text-xs). No "Filtered by…" helper line.
    <div className="grid grid-cols-[auto_1fr_1fr_1fr_1fr] gap-3 border-t border-border/30">
      {/* Region is static (from the selected index) — always shown. The value
          sits absolutely over an invisible "Developed Markets (ex-NA)"
          placeholder so the auto column is reserved at the width of the
          longest label; the four 1fr data columns then stay put across
          index/scope changes instead of shifting with the live region name
          (Figma UniverseSelection.tsx:1786). */}
      <div className="space-y-0.5">
        <p className="text-[15px] text-muted-foreground whitespace-nowrap">
          Region
        </p>
        <div className="font-medium text-sm text-foreground whitespace-nowrap relative">
          <span aria-hidden className="invisible select-none">
            Developed Markets (ex-NA)
          </span>
          <span className="absolute inset-0">{region}</span>
        </div>
      </div>
      {/* Countries and Coverage are published index properties — they render
          from the static catalogue immediately and are superseded by the live
          values, so they never flash a skeleton or a dash. */}
      <StatNumber
        label={stats.countries === 1 ? 'Country' : 'Countries'}
        value={statValue(
          String(stats.countries),
          countriesReduced ? String(stats.countriesInitial) : null,
        )}
        skeletonWidth="w-8"
      />
      <StatNumber
        label="Constituents"
        // Only exclusions/narrowing pull the live count below the universe
        // size; then show "remaining / initial" so the gap is legible.
        value={statValue(
          stats.constituents.toLocaleString(),
          constituentsReduced ? stats.universeSize.toLocaleString() : null,
        )}
        status={metricsStatus}
        skeletonWidth="w-12"
      />
      <StatNumber
        label="Coverage"
        value={statValue(
          formatCoverage(stats.coverage),
          constituentsReduced ? formatCoverage(stats.coverageInitial) : null,
        )}
        skeletonWidth="w-10"
      />
      <StatNumber
        label="Market Cap."
        value={statValue(
          formatMarketCap(stats.marketCap),
          constituentsReduced ? formatMarketCap(stats.marketCapInitial) : null,
        )}
        status={metricsStatus}
        skeletonWidth="w-12"
      />
    </div>
  );
}
